import React, { useEffect } from "react";
import {
  Button,
  Card,
  Col,
  Flex,
  Form,
  Input,
  Row,
  Select,
  Typography,
  Switch,
  DatePicker,
} from "antd";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NotificationOutlined } from "@ant-design/icons";
import styles from "./PushNotification.module.scss";
import { colors } from "../../../../../constants/colors";
import RequiredLabel from "../../../../common/required-label/RequiredLabel";
import Spinner from "../../../../common/spinner/Spinner";
import { sanitizeDataTypeClientSide } from "../../../../../utils/utils";
import TextArea from "antd/es/input/TextArea";
import {
  getOrganizationPushNotificationAction,
  sendOrganizationPushNotificationAction,
} from "../../../../../redux/services/push-notification/pushNotificationServices";
import { appConstConfig } from "../../../../../constants/config/appConstConfig";
import {
  getCityAction,
  getCountryAction,
  getStateAction,
} from "../../../../../redux/services/auth/authServices";
import {
  cleanupCityList,
  cleanupStateList,
} from "../../../../../redux/reducers/authReducer";
import dayjs from "dayjs";

const SendPushNotification = () => {
  /* i18n dependencies */
  const { t } = useTranslation();
  /* i18n dependencies */

  /* dispatch dependencies */
  const dispatch = useDispatch();
  /* dispatch dependencies */

  /* loading dependencies */
  const [loading, setLoading] = useState(false);
  const [selectListLoader, setSelectListLoader] = useState(false);
  /* loading dependencies */

  /* form dependencies */
  const [form] = Form.useForm();
  const initialValues = {
    title: "",
    message: "",
    title_ar: "",
    message_ar: "",
    country: null,
    state: null,
    city: null,
    dateAndTimeSwitch: null,
  };
  const { pushNotifications } = useSelector((state) => state.pushNotification);

  const { countryList, stateList, cityList } = useSelector(
    (state) => state?.auth
  );
  const [isCountrySelected, setIsCountrySelected] = useState(false);
  const [isStateSelected, setIsStateSelected] = useState(false);
  const [selectedCountryObj, setSelectedCountryObj] = useState(null);
  const [selectedStateObj, setSelectedStateObj] = useState(null);
  const [selectedCityObj, setSelectedCityObj] = useState(null);
  const [scheduleSwitch, setScheduleSwitch] = useState(false);
  const [scheduleDateTime, setScheduleDateTime] = useState(null);

  /* form dependencies */
  const handleGetTableData = () => {
    dispatch(getOrganizationPushNotificationAction(setLoading));
  };

  const selectListApiCalls = () => {
    // Country select list
    dispatch(getCountryAction());
  };

  useEffect(() => {
    handleGetTableData();
    selectListApiCalls(null);
  }, []);

  /* on Country changed */
  const onCountryChanged = (value) => {
    if (value) {
      const selectedCountryObject = countryList?.find(
        (item) => item._id === value
      );
      setSelectedCountryObj(selectedCountryObject);
      if (selectedCountryObject) {
        let stateObject = {
          country_code: selectedCountryObject?.numeric_code,
        };
        dispatch(getStateAction(stateObject));
        dispatch(cleanupStateList());
        dispatch(cleanupCityList());
        setIsCountrySelected(true);
      }
      form.resetFields(["state", "city"]);
    } else {
      dispatch(cleanupStateList());
      dispatch(cleanupCityList());
      setIsCountrySelected(false);
      form.resetFields(["state", "city"]);
    }
  };
  /* on Country changed */

  /* on State changed */
  const onStateChanged = (value) => {
    const selectedStateObject = stateList?.find((item) => item.id === value);
    setSelectedStateObj(selectedStateObject);
    if (selectedStateObject) {
      let dataToBeSent = {
        stateCode: selectedStateObject?.id,
      };
      dispatch(getCityAction(dataToBeSent));
      dispatch(cleanupCityList());
      setIsStateSelected(true);
      form.resetFields(["city"]);
    }
    else {
      setIsStateSelected(false);
      form.resetFields(["city"]);
    }
  };
  /* on State changed */

  /* on City changed */
  const onCityChanged = (value, element) => {
    const selectedCityObject = cityList?.find(
      (item) => item.id === element?.value
    );
    setSelectedCityObj(selectedCityObject);
  };
  /* on City changed */

  /* reset default form fields */
  const resetDefaultFields = () => {
    form.setFieldsValue({ ...initialValues });
    setSelectedCountryObj(null);
    setSelectedStateObj(null);
    setSelectedCityObj(null);
    setScheduleDateTime(null);
    setScheduleSwitch(false);
  };
  /* reset default form fields */

  /* on submit */
  const onSubmit = (values) => {
    if (pushNotifications?.available > 0) {
      let dataToBeSent = {
        // ...values,
        title: values.title,
        message: values.message,
        title_ar: values.title_ar,
        message_ar: values.message_ar,
        country: selectedCountryObj?._id || "",
        state: selectedStateObj?.name || "",
        city: selectedCityObj?.name || "",
        _id:
          pushNotifications?.available > 0
            ? pushNotifications?.data[0]?._id
            : "",
      };

      if (scheduleSwitch) {
        const date = dayjs(scheduleDateTime, {
          format: "YYYY-MM-DD HH:mm:ss",
        })
          .utc()
          .format();
        dataToBeSent.schedule_date_time = date;
      }

      dispatch(
        sendOrganizationPushNotificationAction(dataToBeSent, setLoading)
      ).then(() => {
        handleGetTableData();
      });
    }
    resetDefaultFields();
  };
  /* on submit */

  const sendNotificationDetails = [
    {
      title: t("Modules.Configuration.notificationBalance"),
      available: pushNotifications?.available,
    },
  ];

  const handleScheduleSwitch = (e) => {
    setScheduleSwitch(e);
  };

  const onDateChange = (date, dateString) => {
    // // YYYY-MM-DDTHH:mm:ss
    setScheduleDateTime(dayjs(date)?.format("YYYY-MM-DDTHH:mm:ss"));
  };

  /* start date and end date validation dependencies */
  const disabledStartDate = (currentDate) => {
    return currentDate && dayjs(currentDate)?.isBefore(dayjs(), "day");
  };

  return (
    <div className={styles.configurationContainer}>
      {loading ? (
        <Spinner />
      ) : (
        <Flex vertical gap="1rem">
          <Flex gap="1rem">
            <Typography.Title level={2}>{`${t(
              "Modules.addOns.SendNotification"
            )}`}</Typography.Title>
          </Flex>
          <Flex gap="1rem">
            {sendNotificationDetails?.map((element) => {
              return (
                <Card
                  bordered
                  hoverable
                  style={{
                    width: 300,
                    padding: 0,
                    borderRadius: 20,
                    border: `1px solid ${colors.oceanBlue}`,
                  }}
                >
                  <Row gutter={[24, 24]} align="middle" justify="center">
                    <Col span={6}>
                      <Button
                        type="primary"
                        shape="circle"
                        size="middle"
                        icon={<NotificationOutlined style={{ fontSize: 22 }} />}
                        style={{ background: colors.oceanBlue }}
                      />
                    </Col>
                    <Col span={18}>
                      <Flex vertical>
                        <Typography.Title level={3}>
                          {element.title}
                        </Typography.Title>
                        <Typography.Title
                          level={2}
                          style={{ margin: 0, color: colors.DarkMint }}
                        >
                          {`${element?.available}`}
                        </Typography.Title>
                      </Flex>
                    </Col>
                  </Row>
                </Card>
              );
            })}
          </Flex>
          <Flex vertical gap="2rem">
            <Form
              form={form}
              layout="vertical"
              autoComplete="off"
              requiredMark={false}
              // style={{ maxWidth: "00px" }}
              onFinish={onSubmit}
              initialValues={initialValues}
            >
              <Row gutter={[24, 24]}>
                <Col span={8}>
                  <Form.Item
                    label={
                      <RequiredLabel
                        text={t("ProfileSetup.countryLabel")}
                        required={true}
                      />
                    }
                    name="country"
                    rules={[
                      {
                        required: true,
                        message: t("ProfileSetup.countryErrorMessage"),
                      },
                    ]}
                  >
                    <Select
                      placeholder={t("ProfileSetup.countryPlaceholder")}
                      popupMatchSelectWidth={true}
                      onChange={(value, element) => {
                        onCountryChanged(value, element);
                      }}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.children ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    >
                      {countryList &&
                        countryList?.map((item) => (
                          <Select.Option key={item?._id} value={item?._id}>
                            {item?.name}
                          </Select.Option>
                        ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label={
                      <RequiredLabel
                        text={t("ProfileSetup.stateProviceLabel")}
                        required={false}
                      />
                    }
                    name="state"
                  >
                    <Select
                      placeholder={t("ProfileSetup.stateProvicePlaceholder")}
                      popupMatchSelectWidth={true}
                      onChange={(value, element) => {
                        onStateChanged(value, element);
                      }}
                      disabled={!isCountrySelected}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.children ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    >
                      {stateList?.map((item) => (
                      <Select.Option key={item?.id} value={item?.id}>
                        {item?.name}
                      </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label={
                      <RequiredLabel
                        text={t("ProfileSetup.cityLabel")}
                        required={false}
                      />
                    }
                    name="city"
                  >
                    <Select
                      placeholder={t("ProfileSetup.cityPlaceholder")}
                      onChange={(value, element) => {
                        onCityChanged(value, element);
                      }}
                      popupMatchSelectWidth={true}
                      disabled={!isStateSelected}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.children ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    >
                      {cityList?.map((item) => (
                        <Select.Option key={item?.id} value={item?.id}>
                          {item?.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={scheduleSwitch ? 4 : 8}>
                  <Form.Item
                    label={t("CommonUtils.scheduleNotification")}
                    name="scheduleSwitch"
                  >
                    <Switch
                      onChange={(e) => handleScheduleSwitch(e)}
                      value={scheduleSwitch}
                    />
                  </Form.Item>
                </Col>
                {scheduleSwitch && (
                  <Col span={4}>
                    <Form.Item
                      label={
                      <RequiredLabel
                      text={t("CommonUtils.scheduleNotification")}
                      required={true}
                    />
                    }
                      name="dateAndTimeSwitch"
                      rules={[
                        {
                          required: scheduleSwitch ? true : false,
                          message: t("CommonUtils.pleaseSelectScheduleNotificationDate")
                        }
                      ]}
                    >
                      <DatePicker
                        format="DD/MM/YYYY hh:mm A"
                        onChange={(date, dateString) =>
                          onDateChange(date, dateString)
                        }
                        style={{ width: "100%" }}
                        disabledDate={disabledStartDate}
                        showTime={{
                          use12Hours: true,
                          format: "HH:mm",
                          minuteStep: 5,
                        }}
                      />
                    </Form.Item>
                  </Col>
                )}
              </Row>
              <Col span={8}>
                <Form.Item
                  label={
                    <RequiredLabel
                      text={t("Modules.Notify.title")}
                      required={true}
                    />
                  }
                  name="title"
                  rules={[
                    {
                      required: true,
                      message: t("Modules.Notify.pleaseEnterTitle"),
                    },
                    {
                      whitespace: true,
                      message: t("Modules.Notify.pleaseEnterTitle"),
                    },
                  ]}
                  normalize={(value) => sanitizeDataTypeClientSide(value)}
                >
                  <Input
                    placeholder={t("Modules.Notify.enterTitle")}
                    maxLength={
                      appConstConfig.DEFAULT_CHARACTERS_ALLOWED_FOR_TEXTAREA
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
              <Form.Item
                    label={
                      <RequiredLabel
                        text={`${t("Modules.Notify.title")} ${t("CommonUtils.inArabic")}`}
                        required={true}
                      />
                    }
                    name="title_ar"
                    rules={[
                      {
                        required: true,
                        message: `${t("Modules.Notify.pleaseEnterTitle")} ${t("CommonUtils.inArabic")}`,
                      },
                      {
                        whitespace: true,
                        message: `${t("Modules.Notify.pleaseEnterTitle")} ${t("CommonUtils.inArabic")}`,
                      },
                    ]}
                    normalize={(value) => sanitizeDataTypeClientSide(value)}
                  >
                    <Input
                      dir="rtl"
                      placeholder={`${t("Modules.Notify.enterTitle")} ${t("CommonUtils.inArabic")}`}
                    />
                  </Form.Item>
              </Col>
              <>
                <Col span={8}>
                  <Form.Item
                    label={
                      <RequiredLabel
                        text={t("Modules.Notify.description")}
                        required={true}
                      />
                    }
                    name="message"
                    rules={[
                      {
                        required: true,
                        message: t("Modules.Notify.pleaseEnterDescription"),
                      },
                      {
                        whitespace: true,
                        message: t("Modules.Notify.pleaseEnterDescription"),
                      },
                    ]}
                    // normalize={(value) => sanitizeDataTypeClientSide(value)}
                  >
                    <TextArea
                      rows={4}
                      showCount
                      maxLength={
                        appConstConfig.DEFAULT_CHARACTERS_ALLOWED_FOR_TEXTAREA
                      }
                      placeholder={t(
                        "CommonUtils.enterDescriptionUpto255Characters"
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                <Form.Item
                    label={
                      <RequiredLabel
                        text={
                          `${t("Modules.Services.ServicesForm.description")} ${t("CommonUtils.inArabic")}`
                        }
                        required={true}
                      />
                    }
                    name="message_ar"
                    // normalize={(value) => sanitizeDataTypeClientSide(value)}
                    rules={[
                      {
                        required: true,
                        message: `${t(
                          "Modules.Services.ServicesForm.pleaseEnterDescription"
                        )} ${t("CommonUtils.inArabic")}`,
                      },
                      {
                        whitespace: true,
                        message: `${t(
                          "Modules.Services.ServicesForm.pleaseEnterDescription"
                        )} ${t("CommonUtils.inArabic")}`,
                      },
                    ]}
                  >
                    <TextArea
                      dir="rtl"
                      rows={4}
                      showCount
                      maxLength={
                        appConstConfig.DEFAULT_CHARACTERS_ALLOWED_FOR_TEXTAREA
                      }
                      placeholder={`${t(
                        "CommonUtils.enterDescriptionUpto255Characters"
                      )} ${t("CommonUtils.inArabic")}`}
                    />
                  </Form.Item>
                </Col>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  disabled={loading || pushNotifications?.available <= 0}
                  style={{ marginTop: "10px" }}
                >
                  {t("CommonUtils.send")}
                </Button>
              </>
            </Form>
          </Flex>
        </Flex>
      )}
    </div>
  );
};

export default SendPushNotification;
